import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';

/**
 * Componente reutilizable para estados vacíos
 * 
 * Proporciona una UI consistente cuando no hay datos para mostrar, con:
 * - Icono personalizable
 * - Título y descripción
 * - Botones de acción opcionales
 * - Contenido adicional personalizado
 * 
 * @example
 * // Empty state simple
 * <app-empty-state
 *   icon="inbox"
 *   title="No hay miembros"
 *   description="Comienza creando un nuevo miembro para esta comunidad">
 * </app-empty-state>
 * 
 * @example
 * // Con botón de acción
 * <app-empty-state
 *   icon="person_add"
 *   title="No hay miembros"
 *   description="Comienza creando un nuevo miembro"
 *   [primaryButton]="{ label: 'Crear Miembro', icon: 'add' }"
 *   (primaryAction)="onCreateMember()">
 * </app-empty-state>
 * 
 * @example
 * // Con múltiples botones y contenido personalizado
 * <app-empty-state
 *   icon="search_off"
 *   title="Sin resultados"
 *   [primaryButton]="{ label: 'Limpiar filtros', icon: 'clear' }"
 *   [secondaryButton]="{ label: 'Volver', icon: 'arrow_back' }"
 *   (primaryAction)="onClear()"
 *   (secondaryAction)="onBack()">
 *   <div custom-content>
 *     <p>Tu búsqueda no produjo resultados. Intenta con otros términos.</p>
 *   </div>
 * </app-empty-state>
 */
@Component({
  selector: 'app-empty-state',
  templateUrl: './empty-state.component.html',
  styleUrl: './empty-state.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule]
})
export class EmptyStateComponent {
  /**
   * Ícono Material a mostrar. Por defecto: 'inbox'
   * @example icon="search_off"
   */
  @Input() icon: string = 'inbox';

  /**
   * Título del empty state
   */
  @Input() title: string = 'Sin datos';

  /**
   * Descripción adicional (opcional)
   */
  @Input() description?: string;

  /**
   * Configuración del botón primario
   * Si no se proporciona, el botón no se mostrará
   */
  @Input() primaryButton?: { label: string; icon?: string };

  /**
   * Configuración del botón secundario
   * Si no se proporciona, el botón no se mostrará
   */
  @Input() secondaryButton?: { label: string; icon?: string };

  /**
   * Evento cuando se hace click en el botón primario
   */
  @Output() primaryAction = new EventEmitter<void>();

  /**
   * Evento cuando se hace click en el botón secundario
   */
  @Output() secondaryAction = new EventEmitter<void>();

  /**
   * Dispara el evento del botón primario
   */
  onPrimaryClick(): void {
    this.primaryAction.emit();
  }

  /**
   * Dispara el evento del botón secundario
   */
  onSecondaryClick(): void {
    this.secondaryAction.emit();
  }
}
